import type { IconPrimitives, IconProps } from './types';

/**
 * Extra Needs + Modifiers icon set — paths extracted from 2026 Kai Styleguide
 * Figma (Streamline-Cyber source library). PNG-only glyphs retain their
 * existing hand-coded implementations.
 */
export const buildExtraNeedsIcons = ({ Svg, Path, Circle, Rect, Ellipse, defaultColor }: IconPrimitives) => ({
  // ── PNG-only icons (kept from existing implementations) ────────────────
  PauseIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
      <Circle cx='12' cy='12' r='6.5' stroke={color} strokeWidth='1.25' />
      <Path d='M10.5 9.75V14.25' stroke={color} strokeWidth='1.25' strokeLinecap='round' />
      <Path d='M13.5 9.75V14.25' stroke={color} strokeWidth='1.25' strokeLinecap='round' />
    </Svg>
  ),
  StopIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
      <Path d='M9.5 5.75H14.5L17.75 12L14.5 18.25H9.5L6.25 12L9.5 5.75Z' stroke={color} strokeWidth='1.25' strokeLinejoin='round' />
    </Svg>
  ),
  LockIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
      <Path
        d='M8.5 11.5V9.75C8.5 7.95507 9.95507 6.5 11.75 6.5H12.25C14.0449 6.5 15.5 7.95507 15.5 9.75V11.5'
        stroke={color}
        strokeWidth='1.25'
        strokeLinecap='round'
      />
      <Rect x='7' y='11.5' width='10' height='7' rx='1.25' stroke={color} strokeWidth='1.25' />
      <Circle cx='12' cy='14.25' r='1' fill={color} />
      <Path d='M12 15V16.75' stroke={color} strokeWidth='1.25' strokeLinecap='round' />
    </Svg>
  ),
  ImageUnavailableIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
      <Path
        d='M8.75 17.5H15.75C17.5449 17.5 19 16.0449 19 14.25C19 12.6121 17.7794 11.2593 16.1962 11.0513C15.8314 9.53381 14.4659 8.5 12.875 8.5C10.9953 8.5 9.44151 9.94392 9.26666 11.7788C7.47513 11.9405 6.07143 13.4467 6.07143 15.2809C6.07143 16.498 7.06066 17.5 8.27778 17.5'
        stroke={color}
        strokeWidth='1.25'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <Path d='M7.5 8L16.5 17' stroke={color} strokeWidth='1.25' strokeLinecap='round' />
    </Svg>
  ),
  MissingPhotoIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 22 22' fill='none' {...props}>
      <Path d='M21.5 0.5H0.5V21.5H21.5V0.5Z' stroke={color} strokeMiterlimit={10} />
      <Path d='M9.3421 8.51316L7.13158 9.89474L4.92105 8.51316V6.30263L7.13158 4.92105L9.3421 6.30263V8.51316Z' stroke={color} strokeMiterlimit={10} />
      <Path d='M14.3158 8.23684L11 13.7632L8.23684 12.6579L6.02632 15.9737H17.0789L14.3158 8.23684Z' stroke={color} strokeMiterlimit={10} />
      <Path d='M2 2L20 20' stroke={color} strokeMiterlimit={10} />
    </Svg>
  ),
  GroupIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
      <Circle cx='8' cy='8.5' r='2' stroke={color} />
      <Circle cx='16' cy='8.5' r='2' stroke={color} />
      <Circle cx='12' cy='11.5' r='2.5' stroke={color} />
      <Path d='M4.5 18C4.5 16.6193 6.067 15.5 8 15.5' stroke={color} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M15.9999 15.5C17.9329 15.5 19.4999 16.6193 19.4999 18' stroke={color} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M8 18C8 16.067 9.79086 14.5 12 14.5C14.2091 14.5 16 16.067 16 18' stroke={color} strokeLinecap='round' strokeLinejoin='round' />
    </Svg>
  ),
  TradesIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
      <Path
        d='M14.25 7.25L16.75 4.75C15.7057 4.43402 14.5255 4.69485 13.6956 5.52479C12.7544 6.46597 12.5714 7.90856 13.1466 9.03898L8.03898 14.1466C6.90856 13.5714 5.46597 13.7544 4.52479 14.6956C3.69485 15.5255 3.43402 16.7057 3.75 17.75L6.25 15.25L8.5 15.5L8.75 17.75L6.25 20.25C7.29428 20.566 8.4745 20.3052 9.30444 19.4752C10.2456 18.534 10.4286 17.0914 9.8534 15.961L14.961 10.8534C16.0914 11.4286 17.534 11.2456 18.4752 10.3044C19.3052 9.4745 19.566 8.29428 19.25 7.25L16.75 9.75L14.5 9.5L14.25 7.25Z'
        stroke={color}
        strokeWidth='1.25'
        strokeLinejoin='round'
      />
    </Svg>
  ),
  StorageIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => {
    const transformValue = 'scale(0.5454545455)';
    return (
      <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
        <Path
          d='M11.2393 30.8738V14.288C11.2393 14.288 13.0588 10 22.1566 10C31.2543 10 33.1565 14.288 33.1565 14.288V30.8738C33.1565 30.8738 30.8408 35 22.1566 35C13.4723 35 11.2393 30.8738 11.2393 30.8738Z'
          stroke={color}
          transform={transformValue}
        />
        <Path
          d='M11.2393 18.4951C11.2393 18.4951 13.4723 21.3268 22.0738 21.3268C30.6753 21.3268 33.2393 18.4951 33.2393 18.4951'
          stroke={color}
          transform={transformValue}
        />
        <Path
          d='M11.2393 24.9673C11.2393 24.9673 13.4723 27.799 22.0738 27.799C30.6753 27.799 33.2393 24.9673 33.2393 24.9673'
          stroke={color}
          transform={transformValue}
        />
      </Svg>
    );
  },

  // ── Streamline-Cyber icons (2026 Kai Styleguide) ────────────────────────
  PhotoIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 22 22' fill='none' {...props}>
      <Path d='M21.5 0.5H0.5V21.5H21.5V0.5Z' stroke={color} strokeMiterlimit={10} />
      <Path d='M9.3421 8.51316L7.13158 9.89474L4.92105 8.51316V6.30263L7.13158 4.92105L9.3421 6.30263V8.51316Z' stroke={color} strokeMiterlimit={10} />
      <Path d='M14.3158 8.23684L11 13.7632L8.23684 12.6579L6.02632 15.9737H17.0789L14.3158 8.23684Z' stroke={color} strokeMiterlimit={10} />
    </Svg>
  ),
  VideoIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
      <Path d='M15.5 11.113V5.5H0.5V19.5H15.5V14.887L22.5 17.954V8.046L15.5 11.113Z' stroke={color} strokeMiterlimit={10} />
    </Svg>
  ),
  InsightTipIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
      <Path d='M0.5 12.541H23.5' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M5.71219 18.6208L18.2878 6.46126' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M11.9998 24.0408L12.0002 1.04128' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M18.2878 18.6208L5.71219 6.46126' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
    </Svg>
  ),
  OutOfStockItemIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
      <Path d='M22.5 1.5H13L0.5 14L10 23.5L22.5 11V1.5Z' stroke={color} strokeMiterlimit={10} />
      <Path d='M20.501 3.5H17.501V6.5H20.501V3.5Z' stroke={color} strokeMiterlimit={10} />
      <Path d='M2 2L22 22' stroke={color} strokeMiterlimit={10} />
    </Svg>
  ),
  UnassignedContractorIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
      <Path d='M12.9992 0.500218L7.99927 5.50012L18.499 15.9998L23.4989 10.9999L12.9992 0.500218Z' stroke={color} strokeMiterlimit={10} />
      <Path d='M14.25 11.75L2.5 23.5L1 23L0.5 21.5L12.25 9.75' stroke={color} strokeMiterlimit={10} />
      <Path d='M17.25 4.75L19 3L21 5L19.25 6.75' stroke={color} strokeMiterlimit={10} strokeLinejoin='round' />
      <Path d='M2 2L22 22' stroke={color} strokeMiterlimit={10} />
    </Svg>
  ),
  BatterySaveModeIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
      <Path d='M13.5 18.5H20.5V14.5H23.5V10.5H20.5V6.5H13.5' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M7.501 6.5H0.5V18.5H7.5' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M9.00355 19.0595L10.7157 12.6696' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path
        d='M12.6179 15.8984L9.51732 17.1429L7.45439 14.5148L8.6225 10.5505L10.6685 9.3693L11.3673 6.79981L14.2023 11.7101L12.6179 15.8984Z'
        stroke={color}
        strokeMiterlimit={10}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </Svg>
  ),
  ArchiveIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
      <Path d='M22.5 16.5H16.5L14.5 19.5H8.5L6.5 16.5H0.5V23.5H22.5V16.5Z' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M22.5 16.5L20.5 9.5H2.5L0.5 16.5' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
    </Svg>
  ),
  ProjectsIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
      <Path d='M22.5 11L11.5 17.5L0.5 11' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M22.5 14L11.5 20.5L0.5 14' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M22.5 17L11.5 23.5L0.5 17' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M11.5 14.5L0.5 8L11.5 1.5L22.5 8L11.5 14.5Z' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
    </Svg>
  ),
  DuplicateIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
      <Path d='M16.5 1.5H0.5V17.5H16.5V1.5Z' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M7.5 23.5H6.5V22.5' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M22.5 22.5V23.5H21.5' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M21.5 7.5H22.5V8.5' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M19.5 23.5H18.5' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M19.5 7.5H18.5' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M16.5 23.5H15.5' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M13.5 23.5H12.5' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M10.5 23.5H9.5' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M22.5 20.5V19.5' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M6.5 20.5V19.5' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M22.5 17.5V16.5' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M22.5 14.5V13.5' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M22.5 11.5V10.5' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
    </Svg>
  ),

  // ── Modifiers group (NEW — 2026 Kai Styleguide) ─────────────────────────
  TenantResponsibleIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
      {/* Key shape (bottom layer) */}
      <Path
        d='M19.3616 5.30436L17.3026 3.24535H8.81193L7.43925 0.5H3.32123L0.575876 5.30436L3.32123 10.1087H7.43925L8.81193 7.36338H17.3026L19.3616 5.30436Z'
        stroke={color}
        strokeMiterlimit={10}
      />
      <Path d='M5.38029 7.36344L4.00761 5.30443L5.38029 3.24542' stroke={color} strokeMiterlimit={10} />
      {/* House shape (top layer) */}
      <Path d='M7.72227 4.18483H4.83338L4.11116 5.62928L4.83338 7.07372H7.72227L8.44449 8.51817L7.72227 9.96261H4.83338' stroke={color} strokeMiterlimit={10} />
      <Path d='M6.27779 4.18474V3.46251' stroke={color} strokeMiterlimit={10} />
      <Path d='M6.27779 10.6847V9.96251' stroke={color} strokeMiterlimit={10} />
      <Path d='M12.0556 10.3237L6.27778 13.5737L0.5 10.3237V3.82367L6.27778 0.573674L12.0556 3.82367V10.3237Z' stroke={color} strokeMiterlimit={10} />
    </Svg>
  ),
  DeferredIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
      <Path d='M11.5 7.5V13.5H15.5' stroke={color} strokeMiterlimit={10} />
      <Path d='M9.5 22.5H17L22.5 12.5L17 2.5H6L1.875 10' stroke={color} strokeMiterlimit={10} />
      <Path d='M0.5 5.26294L1.875 9.99994L6.677 8.60594' stroke={color} strokeMiterlimit={10} />
    </Svg>
  ),
});
